/**
 * CascadeRow  (SM-21 / DA-2294)
 *
 * The reusable cascading-picker framework itself. Given an ordered list of steps —
 * each step's `options` already computed by the caller against the mapper grammar
 * catalog for the *current* values (target → operator → value, per profile) — this
 * renders them left-to-right, gates step N on step N-1 having a value, and clears
 * every downstream value the instant an upstream one changes (its options are no
 * longer valid otherwise). HP (SM-22) and AP (SM-23) plug their own step
 * definitions into this; it carries no profile-specific knowledge.
 */
import React from 'react'
import { Box } from '@mui/material'
import { ChevronRight } from 'lucide-react'
import { CascadeStep } from './CascadeStep'
import { CascadeStepDef, CascadeValues } from './cascadeTypes'

export interface CascadeRowProps {
  steps: CascadeStepDef[]
  values: CascadeValues
  onChange: (values: CascadeValues) => void
  disabled?: boolean
  /** Catalog still loading — threaded to each step so options aren't misread as "empty". */
  loading?: boolean
}

export const CascadeRow: React.FC<CascadeRowProps> = ({ steps, values, onChange, disabled = false, loading = false }) => {

  const handleStepChange = (index: number, newValue: string) => {
    const updated: CascadeValues = { ...values, [steps[index].key]: newValue }
    // Every step after the one that changed depended on it — invalidate them.
    for (let i = index + 1; i < steps.length; i++) delete updated[steps[i].key]
    onChange(updated)
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      {steps.map((step, index) => {
        // Step 0 is always available; step N needs step N-1 resolved first. A `locked`
        // upstream step (forward-guard, DA-2184) counts as resolved — its target is pinned
        // by the question scope, so downstream steps must not be gated off it.
        const prev = index > 0 ? steps[index - 1] : undefined
        const isGated = index > 0 && !prev!.locked && !values[prev!.key]
        return (
          <React.Fragment key={step.key}>
            {index > 0 && (
              <ChevronRight size={16} aria-hidden focusable={false} style={{ marginTop: 20, opacity: 0.4, flexShrink: 0 }} />
            )}
            <CascadeStep
              step={step}
              value={values[step.key] ?? ''}
              onChange={(v) => handleStepChange(index, v)}
              disabled={disabled || isGated}
              loading={loading}
            />
          </React.Fragment>
        )
      })}
    </Box>
  )
}
